import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ShowcaseWorkflowStateViewStore } from "../../store";
import { ShowcaseWorkflowStateView } from "../view/ShowcaseWorkflowStateView";
import { ShowcaseWorkflowStateUpdateStore } from "../../store";
import { ShowcaseWorkflowStateUpdate } from "../update/ShowcaseWorkflowStateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ShowcaseWorkflowStateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ShowcaseWorkflowStateModalContent: React.FC<ShowcaseWorkflowStateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ShowcaseWorkflowStateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ShowcaseWorkflowStateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ShowcaseWorkflowStateView
            showcaseWorkflowStateViewStore={
              storeInfo.store as ShowcaseWorkflowStateViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <ShowcaseWorkflowStateUpdate
            showcaseWorkflowStateUpdateStore={
              storeInfo.store as ShowcaseWorkflowStateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
